import React, { useState, useContext } from 'react';
import { SimplifiedChampion } from '../../XXX/gameTypes';
import StaticAddChampion, { StaticChampionAddData } from '../../StaticSandbox/components/StaticAddChampion';
import { staticDataService } from '../../StaticSandbox/services/staticDataService';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import { MapChampion, createMapChampion } from './mapTypes';
import './MapChampionPanel.css';

interface MapChampionPanelProps {
  champions: MapChampion[];
  onChampionsChange: (fn: MapChampion[] | ((prev: MapChampion[]) => MapChampion[])) => void;
  selectedChampionIdx: number | null;
  onSelectChampion: (idx: number | null) => void;
}

const MapChampionPanel: React.FC<MapChampionPanelProps> = ({
  champions,
  onChampionsChange,
  selectedChampionIdx,
  onSelectChampion,
}) => {
  const [showAddChampion, setShowAddChampion] = useState(false);
  const [addingTeam, setAddingTeam] = useState<'blue' | 'red'>('blue');
  const [isLoading, setIsLoading] = useState(false);

  const { championsAssets } = useContext(GameAssetsContext) || {};

  /** Add a new champion */
  const handleAddChampion = async (data: StaticChampionAddData) => {
    setIsLoading(true);
    try {
      const champ = await staticDataService.fetchChampionAtLevel(
        data.name,
        data.level,
        data.team,
      );
      const newChampion = createMapChampion(champ, champions.length);
      onChampionsChange([...champions, newChampion]);
      setShowAddChampion(false);
    } catch (err) {
      console.error('Error adding champion:', err);
      alert('Failed to add champion. Make sure the backend is running.');
    } finally {
      setIsLoading(false);
    }
  };

  /** Remove a champion */
  const handleRemoveChampion = (idx: number) => {
    const updated = champions.filter((_, i) => i !== idx);
    onChampionsChange(updated);
    if (selectedChampionIdx === idx) onSelectChampion(null);
    else if (selectedChampionIdx !== null && selectedChampionIdx > idx) {
      onSelectChampion(selectedChampionIdx - 1);
    }
  };

  /** Toggle show/hide path for a champion */
  const handleTogglePath = (idx: number) => {
    const updated = [...champions];
    updated[idx] = { ...updated[idx], showPath: !updated[idx].showPath };
    onChampionsChange(updated);
  };

  /** Get movement speed */
  const getMS = (champ: SimplifiedChampion): number => {
    return champ.stats?.totalStats?.movementSpeed ?? 325;
  };

  return (
    <div className="mcp-panel">
      <div className="mcp-header">
        <h3 className="mcp-title">Champions</h3>
        <div className="mcp-add-buttons">
          <button
            className="mcp-add-btn mcp-blue"
            onClick={() => { setAddingTeam('blue'); setShowAddChampion(true); }}
          >
            + Blue
          </button>
          <button
            className="mcp-add-btn mcp-red"
            onClick={() => { setAddingTeam('red'); setShowAddChampion(true); }}
          >
            + Red
          </button>
        </div>
      </div>

      {showAddChampion && (
        <div className="mcp-add-overlay">
          <StaticAddChampion
            onSelectChampion={handleAddChampion}
            onCancel={() => setShowAddChampion(false)}
            defaultTeam={addingTeam}
          />
        </div>
      )}

      {isLoading && (
        <div className="mcp-loading">
          <span>Loading champion...</span>
        </div>
      )}

      <div className="mcp-list">
        {champions.length === 0 && !showAddChampion && (
          <div className="mcp-empty">
            Add champions to place them on the map. Left-click to select &amp; drag, right-click to pathfind.
          </div>
        )}

        {champions.map((mc, idx) => {
          const ms = getMS(mc.champion);
          const isSelected = selectedChampionIdx === idx;
          const champImg = championsAssets?.[mc.champion.name]?.championImageUrl || mc.champion.imageUrl;
          const hasPath = mc.pathSegments.length > 0;

          return (
            <div
              key={mc.champion.id + '-' + idx}
              className={`mcp-entry ${isSelected ? 'selected' : ''}`}
              style={{ borderBottomColor: isSelected ? mc.color : undefined } as React.CSSProperties}
            >
              <div
                className="mcp-row"
                onClick={() => onSelectChampion(isSelected ? null : idx)}
              >
                <div className="mcp-icon-wrap" style={{ borderColor: mc.color }}>
                  <img src={champImg} alt={mc.champion.name} className="mcp-icon" />
                  <span className="mcp-lvl">{mc.champion.level}</span>
                </div>

                <div className="mcp-info">
                  <span className="mcp-name">{mc.champion.name}</span>
                  <span className={`mcp-team ${mc.champion.team}`}>
                    {mc.champion.team === 'blue' ? 'Blue' : 'Red'}
                  </span>
                </div>

                <div className="mcp-ms-badge">
                  <span className="mcp-ms-val">{Math.round(ms)}</span>
                  <span className="mcp-ms-lbl">MS</span>
                </div>

                {mc.travelTimeSeconds !== null && (
                  <div className="mcp-time-badge">
                    <span className="mcp-time-val">
                      {mc.travelTimeSeconds < 60
                        ? `${mc.travelTimeSeconds.toFixed(1)}s`
                        : `${Math.floor(mc.travelTimeSeconds / 60)}m${(mc.travelTimeSeconds % 60).toFixed(0)}s`}
                    </span>
                  </div>
                )}

                <div className="mcp-actions">
                  {hasPath && (
                    <button
                      className={`mcp-action-btn ${mc.showPath ? 'mcp-path-on' : 'mcp-path-off'}`}
                      onClick={(e) => { e.stopPropagation(); handleTogglePath(idx); }}
                      title={mc.showPath ? 'Hide path' : 'Show path'}
                    >
                      {mc.showPath ? '👁' : '👁‍🗨'}
                    </button>
                  )}
                  <button
                    className="mcp-action-btn mcp-remove"
                    onClick={(e) => { e.stopPropagation(); handleRemoveChampion(idx); }}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MapChampionPanel;
